import React from 'react'
import { useTheme } from '@mui/material/styles'
import {
  Card,
  CardContent,
  Stack,
  Box,
  Typography,
  Chip,
  IconButton,
} from '@mui/material'
import {
  LocationOn as LocationOnIcon,
  AttachMoney as AttachMoneyIcon,
  Business as BusinessIcon,
  AutoAwesome as AutoAwesomeIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from '@mui/icons-material'
import { formatSalary, getPracticeTypeString } from './helpers'
import useJobFeedback from './JobFeedback'

// Match tier color mapping
const getMatchTierColor = (matchTier, theme) => {
  switch (matchTier?.toLowerCase()) {
    case 'excellent':
      return {
        bg: theme.palette.success.light,
        color: theme.palette.success.dark,
        border: theme.palette.success.main,
      }
    case 'strong':
      return {
        bg: theme.palette.info.light,
        color: theme.palette.info.dark,
        border: theme.palette.info.main,
      }
    case 'good':
      return {
        bg: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        border: theme.palette.warning.main,
      }
    case 'moderate':
    default:
      return {
        bg: theme.palette.grey[200],
        color: theme.palette.grey[700],
        border: theme.palette.grey[400],
      }
  }
}

const stripHtmlTags = (html) => {
  if (!html) return ''
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
}

const AIJobCard = ({
  rankedJob,
  isActive,
  onClick,
  conversationId,
  onFavoriteClick,
  isFavorite,
  isMobile,
}) => {
  const theme = useTheme()

  const job = rankedJob?.job
  const matchTier = rankedJob?.matchTier
  const justification = rankedJob?.justification

  if (!job) return null

  const matchTierColors = getMatchTierColor(matchTier, theme)
  const descriptionText = stripHtmlTags(job.about || job.aboutRaw)
  const salaryDisplay = formatSalary(job.salary || job.salaryMin || job.salaryMax)

  const feedbackElements = useJobFeedback({
    jobId: job.id,
    conversationId,
    chatbotMessageId: rankedJob.chatbotMessageId,
    matchTier,
    feedback: rankedJob.feedback,
  })

  return (
    <Card
      onClick={onClick}
      sx={{
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        flexShrink: 0,
        borderRadius: 1,
        border: isActive
          ? `2px solid ${theme.palette.ai.main}`
          : `1px solid ${theme.palette.divider}`,
        boxShadow: isActive ? theme.palette.shadows.medium : theme.palette.shadows.light,
        cursor: 'pointer',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
        '&:hover': {
          boxShadow: theme.palette.shadows.medium,
          borderColor: theme.palette.ai.light,
          transition: 'all 0.2s ease-in-out',
          transform: 'translateZ(0)',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 1, md: 1.25 }, '&:last-child': { pb: { xs: 1, md: 1.25 } } }}>
        <Stack spacing={0.5}>
          {/* Row 1: Title + Practice Type + Favorite */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography
              variant='subtitle1'
              sx={{
                color: theme.palette.primary.dark,
                fontWeight: 'medium',
                fontSize: { xs: '0.9rem', md: '0.95rem' },
                lineHeight: 1.3,
                flex: 1,
                minWidth: 0,
              }}
            >
              {job.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flexShrink: 0 }}>
              {job.practiceType && getPracticeTypeString(job.practiceType) && (
                <Chip
                  label={getPracticeTypeString(job.practiceType)}
                  size='small'
                  variant='outlined'
                  sx={{ height: '20px', fontSize: '0.65rem', display: { xs: 'none', md: 'flex' } }}
                />
              )}
              {onFavoriteClick && (
                <IconButton
                  aria-label='favorite'
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation()
                    onFavoriteClick({ jobId: job.id, favorite: !isFavorite })
                  }}
                  sx={{ p: 0.25 }}
                >
                  {isFavorite
                    ? <FavoriteIcon color='error' sx={{ fontSize: '1rem' }} />
                    : <FavoriteBorderIcon color='error' sx={{ fontSize: '1rem' }} />
                  }
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Row 2: Employer · Location · Salary — all on one line */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, color: theme.palette.text.secondary }}>
            {job.employer?.name && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BusinessIcon sx={{ fontSize: '0.85rem' }} color='action' />
                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                  {job.employer.name}
                </Typography>
              </Box>
            )}
            {(job.city || job.state) && (
              <>
                {job.employer?.name && (
                  <Typography variant='body2' sx={{ fontSize: '0.7rem', color: theme.palette.grey[400] }}>·</Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: '0.85rem' }} color='action' />
                  <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                    {job.city}{job.city && job.state && ', '}{job.state}
                  </Typography>
                </Box>
              </>
            )}
            {salaryDisplay && (
              <>
                <Typography variant='body2' sx={{ fontSize: '0.7rem', color: theme.palette.grey[400] }}>·</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AttachMoneyIcon sx={{ fontSize: '0.85rem' }} color='action' />
                  <Typography variant='body2' sx={{ fontSize: '0.8rem', fontWeight: 'medium' }}>
                    {salaryDisplay}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Row 3: 1-line job description */}
          {descriptionText && (
            <Typography
              variant='body2'
              sx={{
                fontSize: '0.78rem',
                color: theme.palette.text.secondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontStyle: 'italic',
                minWidth: 0,
              }}
            >
              {descriptionText}
            </Typography>
          )}

          {/* Mobile-only: Match tier + Practice type row */}
          {isMobile && (matchTier || (job.practiceType && getPracticeTypeString(job.practiceType))) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              {matchTier && (
                <Chip
                  icon={<AutoAwesomeIcon />}
                  label={matchTier}
                  size='small'
                  sx={{
                    height: '24px',
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    backgroundColor: matchTierColors.border,
                    color: 'white',
                    '& .MuiChip-icon': { color: 'white' },
                  }}
                />
              )}
              {job.practiceType && getPracticeTypeString(job.practiceType) && (
                <Chip
                  label={getPracticeTypeString(job.practiceType)}
                  size='small'
                  variant='outlined'
                  sx={{ height: '24px', fontSize: '0.7rem' }}
                />
              )}
            </Box>
          )}

          {/* Row 4: Specialties + Benefits chips — all inline */}
          {(job.specialties?.length > 0 || job.visaSponsorship || job.loanForgiveness || job.pslfCompatible) && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              {job.specialties?.slice(0, isMobile ? 1 : 2).map(specialty => (
                <Chip
                  key={specialty?.id}
                  label={specialty?.name}
                  size='small'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              ))}
              {job.specialties?.length > (isMobile ? 1 : 2) && (
                <Chip
                  label={`+${job.specialties.length - (isMobile ? 1 : 2)}`}
                  size='small'
                  variant='outlined'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              )}
              {job.visaSponsorship && (
                <Chip label='Visa' size='small' variant='outlined' color='success' sx={{ height: '20px', fontSize: '0.65rem' }} />
              )}
              {job.loanForgiveness && (
                <Chip label='Loan Forgive.' size='small' variant='outlined' color='success' sx={{ height: '20px', fontSize: '0.65rem' }} />
              )}
              {job.pslfCompatible && (
                <Chip label='PSLF' size='small' variant='outlined' color='success' sx={{ height: '20px', fontSize: '0.65rem' }} />
              )}
            </Box>
          )}

          {/* Row 5: Match justification — with tier badge, Ask Stethi, and feedback */}
          {(justification || matchTier) && (
            <>
              <Box
                sx={{
                  display: { xs: 'none', md: 'block' },
                  backgroundColor: matchTierColors.bg,
                  borderRadius: 1,
                  p: 0.75,
                  borderLeft: `3px solid ${matchTierColors.border}`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: justification ? 0.25 : 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Chip
                      icon={<AutoAwesomeIcon sx={{ fontSize: '0.75rem !important' }} />}
                      label={matchTier}
                      size='small'
                      sx={{
                        height: '20px',
                        fontSize: '0.65rem',
                        fontWeight: 'bold',
                        backgroundColor: matchTierColors.border,
                        color: 'white',
                        '& .MuiChip-icon': { color: 'white' },
                      }}
                    />
                    {justification && (
                      <Typography
                        variant='caption'
                        sx={{ color: matchTierColors.color, fontWeight: 600 }}
                      >
                        Why this match
                      </Typography>
                    )}
                  </Box>
                  {feedbackElements.thumbs}
                </Box>
                {justification && (
                  <Typography
                    variant='body2'
                    sx={{
                      fontSize: '0.78rem',
                      color: theme.palette.text.secondary,
                      lineHeight: 1.4,
                    }}
                  >
                    {justification}
                  </Typography>
                )}
              </Box>

              {feedbackElements.panel}
            </>
          )}

        </Stack>
      </CardContent>
    </Card>
  )
}

export default AIJobCard
